import React, {useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import './DailyLoginRewardModal.css';
import PageButton from '../../elements/PageButton/PageButton';
import {LoadedImage} from '../../EmblaCarousel/FactionsCarousel';
import {
  DAILY_LOGIN_REWARDS,
  DAILY_LOGIN_REWARDS_PREMIUM,
  getDailyLoginRewardIndex
} from '../../../configs/dailyLoginRewards';
import BuyPremiumButton from '../../elements/BuyPremiumButton/BuyPremiumButton';
import ChestPurchaseModal from '../ChestPurchaseModal/ChestPurchaseModal';
import CrystalsExchangeModal from '../CrystalsExchangeModal/CrystalsExchangeModal';
import NotEnoughCrystalsModal from '../../pages/ShopPage/NotEnoughCrystalsModal/NotEnoughCrystalsModal';
import {useChestPurchase} from '../../../hooks/shop/useChestPurchase';
import {SHOP_CHEST_CONFIGS} from '../../../configs/chests';
import {getChestIconPath} from '../../../utils/inventory/getChestIconPath';
import {getChestDisplayName} from '../../../utils/inventory/getChestDisplayName';
import {getFormattedPrice} from '../../../utils/price/getFormattedPrice';
import {setDailyLoginRewardClaimed, updatePlayerRewards} from '../../../redux/actions';
import {MENU_SOUND_MANAGER} from '../../../scenes/MenuSoundManager';
import {API_BASE} from '../../../utils/apiConfig';
import {addClassNameIfTrue} from "../../../utils/other/addClassNameIfTrue";

const PAGE_ID = 'DailyLoginRewardModal';
const DAYS_PER_WEEK = 7;

function RewardCellContent({reward}) {
  if (reward.chestId) {
    return (
      <LoadedImage
        className="daily-login-reward-cell-chest"
        src={getChestIconPath(reward.chestId)}
        alt={getChestDisplayName(reward.chestId)}
        title={getChestDisplayName(reward.chestId)}
        placeholderSrc="/img/icons/chest.png"
      />
    );
  }
  if (reward.crystals) {
    return (
      <span className="daily-login-reward-cell-value text-crystals">
        {getFormattedPrice(reward.crystals)}
        <img className="daily-login-reward-cell-icon" src="/img/icons/crystal.png" alt="Кристаллы"/>
      </span>
    );
  }
  return (
    <span className="daily-login-reward-cell-value text-coins">
      {getFormattedPrice(reward.numens)}
      <img className="daily-login-reward-cell-icon" src="/img/icons/numens.png" alt="Нумены"/>
    </span>
  );
}

// Подпись «за что дар» под кнопкой: нумены/кристаллы — количество + иконка,
// тем же цветом, что и на плашке дня; сундук — название тем же цветом, каким
// оно выведено на карточке сундука в магазине (chest.textColor)
function ClaimButtonRewardHint({reward}) {
  if (reward.chestId) {
    const chestConfig = SHOP_CHEST_CONFIGS.find((c) => c.id === reward.chestId);
    return (
      <span style={{color: chestConfig?.dailyRewardTextColor || chestConfig?.textColor}}>
        {chestConfig?.name || getChestDisplayName(reward.chestId)}
      </span>
    );
  }
  if (reward.crystals) {
    return (
      <span className="daily-login-reward-claim-hint-value text-crystals">
        {getFormattedPrice(reward.crystals)}
        <img className="daily-login-reward-claim-hint-icon" src="/img/icons/crystal.png" alt="Кристаллы"/>
      </span>
    );
  }
  return (
    <span className="daily-login-reward-claim-hint-value text-coins">
      {getFormattedPrice(reward.numens)}
      <img className="daily-login-reward-claim-hint-icon" src="/img/icons/numens.png" alt="Нумены"/>
    </span>
  );
}

/**
 * Модалка ежедневной награды за вход (главное меню): 4 недели по 7 дней,
 * награда сегодняшнего дня выделена желтым аутлайном. projectedStreak —
 * счетчик дней подряд, КОТОРЫМ станет сегодняшнее получение (вчера получал —
 * стрик + 1, иначе 1); вычисляет родитель (MainMenu) из профиля. Закрывается
 * только кнопкой «Принять дар» — по успешному ответу сервера награда
 * применяется к redux-профилю (баланс/isNewItems/дата/стрик).
 */
function DailyLoginRewardModal({projectedStreak, onClose, onGoToCrystals, onOpenInventory}) {
  const dispatch = useDispatch();
  const auth = useSelector((state) => state.private.auth);
  const [visible, setVisible] = useState(false);
  const [claiming, setClaiming] = useState(false);
  // Клик по награде календаря: сундук — модалка его покупки (как в магазине),
  // нумены/кристаллы — модалка обмена кристаллов на нумены
  const [chestIdForPurchase, setChestIdForPurchase] = useState(null);
  const [isExchangeModalOpen, setIsExchangeModalOpen] = useState(false);
  const {
    handleBuyChest,
    notEnoughCrystalsAmount,
    setNotEnoughCrystalsAmount,
    missingNumensAmount,
    setMissingNumensAmount,
  } = useChestPurchase(auth);

  const premiumExpiresAt = auth.premiumExpiresAt;
  // Статус премиума считается на КАЖДОМ рендере, без заморозки в useState:
  // модалка монтируется по первому появлению auth.id, а premiumExpiresAt
  // может доехать следующим обновлением профиля (например, ответом /me) —
  // замороженное значение показывало бы владельцу премиума обычный календарь
  const isPremiumActive = Boolean(premiumExpiresAt)
    && new Date(premiumExpiresAt).getTime() > Date.now();
  // С активным премиумом премиум-календарь показывается всегда (в том числе
  // когда статус доехал позже открытия); без него — обычный календарь с
  // возможностью посмотреть премиальный по ссылке
  const [isPremiumCalendarSelected, setIsPremiumCalendarSelected] = useState(false);
  const isPremiumCalendarShown = isPremiumActive || isPremiumCalendarSelected;

  useEffect(() => {
    const showTimer = setTimeout(() => setVisible(true), 50);
    return () => clearTimeout(showTimer);
  }, []);

  // Фиксируем на момент открытия: после клейма auth.dailyLoginRewardClaimedAt
  // становится «сегодня», из-за чего projectedStreak в родителе пересчитается
  // и упадет до 1 еще ДО скрытия модалки (сброс подсветки на первый день
  // посреди fade-out) — застывший индекс это игнорирует
  const [todayIndex] = useState(() => getDailyLoginRewardIndex(projectedStreak));
  // Календарь на экране переключаем ссылками, но НАЧИСЛЯЕМАЯ награда зависит
  // только от реального статуса премиума (сервер решает так же) — подпись на
  // кнопке «Принять дар» всегда честная
  const calendarRewards = isPremiumCalendarShown ? DAILY_LOGIN_REWARDS_PREMIUM : DAILY_LOGIN_REWARDS;
  const todayReward = (isPremiumActive ? DAILY_LOGIN_REWARDS_PREMIUM : DAILY_LOGIN_REWARDS)[todayIndex];

  const handleClaim = async () => {
    if (claiming) return;
    setClaiming(true);
    try {
      const response = await fetch(`${API_BASE}/daily-login-reward/claim`, {
        method: 'POST',
        credentials: 'include',
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        // 409 = сегодня уже получено (другая вкладка/устройство) — награду не
        // выдаем, но ОБЯЗАТЕЛЬНО чиним свой профиль актуальными датой и
        // стриком из ответа: иначе локальное состояние осталось бы «награда
        // доступна», и модалка всплывала бы снова при каждом ремаунте меню
        if (response.status !== 409) {
          throw new Error(data.error || 'Не удалось получить награду');
        }
        dispatch(setDailyLoginRewardClaimed(data.claimedAt ?? null, data.streak ?? 0));
      } else {
        const reward = data.reward || {};
        dispatch(updatePlayerRewards(
          reward.numens || 0,
          undefined,
          reward.chestId ? true : undefined,
          reward.crystals || 0,
        ));
        dispatch(setDailyLoginRewardClaimed(data.claimedAt, data.streak));
        if (reward.chestId) {
          MENU_SOUND_MANAGER.playChestOpen();
        } else {
          MENU_SOUND_MANAGER.playCoins(reward.numens || reward.crystals || 0);
        }
      }
      setVisible(false);
      setTimeout(() => onClose?.(), 300);
    } catch (error) {
      console.error('[DailyLoginReward] claim error', error);
      alert(error.message || 'Не удалось получить награду');
      setClaiming(false);
    }
  };

  const weeksCount = Math.ceil(calendarRewards.length / DAYS_PER_WEEK);

  const handleCalendarSwitch = (showPremium) => {
    setIsPremiumCalendarSelected(showPremium);
  };

  const handleRewardCellClick = (reward) => {
    if (reward.chestId) {
      // Тот же звук сундука, что играет плашка магазина при открытии модалки
      MENU_SOUND_MANAGER.playChestOpen();
      setChestIdForPurchase(reward.chestId);
    } else {
      // Стандартный звук нажатия кнопки (как у PageButton)
      MENU_SOUND_MANAGER.playMenuButtonClick();
      setIsExchangeModalOpen(true);
    }
  };

  // Переход в магазин (вкладка кристаллов) или коллекцию закрывает и модалку
  // наград: награда не потеряна — модалка снова откроется при возврате в меню
  const handleGoToCrystals = () => {
    onGoToCrystals?.();
    onClose?.();
  };
  const handleOpenInventory = () => {
    onOpenInventory?.();
    onClose?.();
  };

  return (
    <div
      className={`daily-login-reward-overlay ${visible ? 'daily-login-reward-overlay_visible' : ''}`}
      role="dialog"
      aria-modal="true"
    >
      <div className={`daily-login-reward-modal ${visible ? 'daily-login-reward-modal_visible' : ''}`}>
        <h2 className="daily-login-reward-title">Награда за вход</h2>
        <p className="daily-login-reward-subtitle">
          Заходи ежедневно, не прерываясь.
          <br/>
          Тогда награды становятся ценнее!
        </p>

        <div className="daily-login-reward-weeks">
          {Array.from({length: weeksCount}).map((_, weekIndex) => (
            <div key={weekIndex} className="daily-login-reward-week">
              <div className="daily-login-reward-week-title">Неделя {weekIndex + 1}</div>
              <div className="daily-login-reward-week-days">
                {calendarRewards
                  .slice(weekIndex * DAYS_PER_WEEK, (weekIndex + 1) * DAYS_PER_WEEK)
                  .map((reward, dayIndex) => {
                    const globalIndex = weekIndex * DAYS_PER_WEEK + dayIndex;
                    const isToday = globalIndex === todayIndex;
                    // Дни ДО сегодняшнего в рамках текущего 28-дневного цикла
                    // стрика уже получены — отмечаем чекмарком
                    const isPast = globalIndex < todayIndex;
                    return (
                      <div key={globalIndex} className="daily-login-reward-day">
                        <div
                          className={`daily-login-reward-cell daily-login-reward-cell_clickable ${addClassNameIfTrue(isPast, 'daily-login-reward-cell_is-past')} ${isToday ? 'daily-login-reward-cell_today' : ''}`}
                          onClick={() => handleRewardCellClick(reward)}
                        >
                          <RewardCellContent reward={reward}/>
                          {/*{isPast && (*/}
                          {/*  <span className="daily-login-reward-cell-check" aria-hidden="true"/>*/}
                          {/*)}*/}
                        </div>
                        <span className="daily-login-reward-day-label">День {dayIndex + 1}</span>
                      </div>
                    );
                  })}
              </div>
            </div>
          ))}
        </div>

        {/* С активным премиумом на месте переключателей календарей — статусная
            надпись: пользователь видит, что уже получает премиум-награды */}
        {isPremiumActive && (
          <div className="daily-login-reward-premium-status">
            <img className="daily-login-reward-switch-icon" src="/img/icons/premium.png" alt="Премиум"/>
            <span className="text-coins">Вы получаете Премиум-награды</span>
          </div>
        )}

        {/* Переключатели календарей — только пользователям БЕЗ активного
            премиума (с премиумом сразу показан премиум-календарь без ссылок) */}
        {!isPremiumActive && !isPremiumCalendarShown && (
          <button
            type="button"
            className="daily-login-reward-switch-link"
            onClick={() => handleCalendarSwitch(true)}
          >
            <img className="daily-login-reward-switch-icon" src="/img/icons/premium.png" alt="Премиум"/>
            <span className="text-coins">Посмотреть награды с Премиумом</span>
          </button>
        )}

        {!isPremiumActive && isPremiumCalendarShown && (
          <>
            <p className="daily-login-reward-premium-note">
              При активации Премиума прогресс наград сохранится!
            </p>
            <button
              type="button"
              className="daily-login-reward-switch-link"
              onClick={() => handleCalendarSwitch(false)}
            >
              <span>Обычные награды</span>
            </button>
          </>
        )}

        <div className="main-menu-buttons daily-login-reward-buttons">
          {!isPremiumActive && isPremiumCalendarShown && (
            <BuyPremiumButton pageId={PAGE_ID}/>
          )}

          <PageButton
            className="daily-login-reward-claim-button"
            onClick={handleClaim}
            pageId={PAGE_ID}
            buttonId="claim"
            disabled={claiming}
          >
            <span className="daily-login-reward-claim-button-label">
              {claiming ? 'Получение...' : 'Принять дар'}
            </span>
            <span className="daily-login-reward-claim-button-hint">
              <ClaimButtonRewardHint reward={todayReward}/>
            </span>
          </PageButton>
        </div>
      </div>

      {/* Модалки поверх (внутри stacking context оверлея наград, поэтому
          рисуются над его содержимым независимо от своих z-index) */}
      {chestIdForPurchase && (
        <ChestPurchaseModal
          chest={SHOP_CHEST_CONFIGS.find((c) => c.id === chestIdForPurchase)}
          onClose={() => setChestIdForPurchase(null)}
          onPurchase={handleBuyChest}
          onOpenInventory={handleOpenInventory}
        />
      )}

      {(isExchangeModalOpen || missingNumensAmount != null) && (
        <CrystalsExchangeModal
          onClose={() => {
            setIsExchangeModalOpen(false);
            setMissingNumensAmount(null);
          }}
          onGoToCrystals={handleGoToCrystals}
          missingNumensAmount={missingNumensAmount}
        />
      )}

      {notEnoughCrystalsAmount != null && (
        <NotEnoughCrystalsModal
          missingAmount={notEnoughCrystalsAmount}
          onGoToCrystals={() => {
            setNotEnoughCrystalsAmount(null);
            handleGoToCrystals();
          }}
          onClose={() => setNotEnoughCrystalsAmount(null)}
        />
      )}
    </div>
  );
}

export default DailyLoginRewardModal;
